import { useCallback, useMemo, useState } from 'react'
import { ITDisplayImage } from '../../../../../../../baseComponents/TwinTable/InputTableLayout/InputTable/Components/Displays'
import TwinTrans from '../../../../../../../baseComponents/TwinTrans'
import { RectangularInput, RectangularInputFormatNumberSmall } from '../../../../../../../forms/Input'
import { TwinDictionary } from '../../../../../../../utils/globals/dictionary'
import { ServedOrderTDTy, typeFieldSearchTy, TypeFieldSearchTyKeys } from '../types'
import useTwinTranslation from '../../../../../../../utils/hooks/useTwinTranslation'
import { TwinIconToolTip } from '../../../../../../../baseComponents/TwinIcon'
import { faCheckCircle, faXmarkCircle } from '@fortawesome/pro-light-svg-icons'
import { OrderData } from '../../../../OrderLayout/types'
import { changeErrorMessage, changeSuccessMessage } from '../../../../../../../utils/reducers/reduxDispatch'
import { flushSync } from 'react-dom'
import { findProductByTypeSearch } from '../functions'

interface InfoSelectedProductProps {
    selectedProduct?: TwinDictionary
    servedOrderTD: ServedOrderTDTy
    children?: React.ReactNode
}

const InfoSelectedProduct: React.FC<InfoSelectedProductProps> = ({servedOrderTD, selectedProduct, children}) => {
    if(!selectedProduct){
        return null
    }
    const parsedId = selectedProduct?.id
    return (
        <div className = 'flex gap-x-20'>
            <div>
                <div><TwinTrans transKey='image'>Imagen</TwinTrans></div>
                <ITDisplayImage className='h-27 w-27' value={selectedProduct?.['url']} allRowData={selectedProduct} />
            </div >
            <div>
                <div><TwinTrans transKey='name'>Nombre</TwinTrans></div>
                <RectangularInput readOnly={true} value={selectedProduct.name} />
            </div>
            <div>
                <div><TwinTrans transKey='notes'>Notas</TwinTrans></div>
                <RectangularInput readOnly={true} value={selectedProduct.notes} />
            </div>
            <div>
                <div><TwinTrans transKey='quantityServed'>Cantidad Servida</TwinTrans></div>
                <RectangularInput readOnly={true} value={servedOrderTD.tableData?.[servedOrderTD.selectedProducts[parsedId]?.posTableData]?.quantity || 0} />
            </div>
            <div>
                <div><TwinTrans transKey='quantityDesired'>Cantidad deseada</TwinTrans></div>
                <RectangularInput readOnly={true} value={selectedProduct.desiredQuantity} />
            </div>
            {children}
        </div >
    )
}



interface SelectorProductsOrderTotalProps extends SelectorProductsOrderLogicProps {
    label: string
    servedOrderTD: ServedOrderTDTy
}

export const SelectorProductsOrderTotal: React.FC<SelectorProductsOrderTotalProps> = ({ selectedProduct, servedOrderTD, typeFieldSearch, label, filterWorksheetProductStatusId, ...logic }) => {
    const { searchProduct, setSearchProduct, searchProductAndAddTotal } = useSelectorProductsOrderLogic({ ...logic, typeFieldSearch, filterWorksheetProductStatusId, servedOrderTD })
    return (
        <div className='flex items-center gap-x-20'>
            <div>
                <div>{label}</div>
                <div className='flex items-center'>
                    <RectangularInput className='prepare_order_reference_selector' value={searchProduct} onChange={setSearchProduct} onKeyDown={searchProductAndAddTotal} autoFocus />
                </div>
            </div>
            <InfoSelectedProduct {...{ servedOrderTD, selectedProduct }} />
        </div>
    )
}

interface SelectorProductsOrderGroupProps extends SelectorProductsOrderLogicProps {
    label: string
    selectedProduct?: TwinDictionary
    servedOrderTD: ServedOrderTDTy
}

export const SelectorProductsOrderGroup: React.FC<SelectorProductsOrderGroupProps> = ({ selectedProduct, servedOrderTD, typeFieldSearch, label, filterWorksheetProductStatusId, ...logic }) => {
    const { searchProduct, setSearchProduct, searchProductInOrder, changeSelectProductQuantity, handleAddProductTable, addSelectedProductTable } = useSelectorProductsOrderLogic({ ...logic, typeFieldSearch, selectedProduct, filterWorksheetProductStatusId, servedOrderTD })
    const { t } = useTwinTranslation()
    return (
        <div className='flex items-center gap-x-20'>
            <div>
                <div>{label}</div>
                <div className='flex items-center'>
                    <RectangularInput className='prepare_order_reference_selector' value={!selectedProduct ? searchProduct : selectedProduct[typeFieldSearchTy[typeFieldSearch]]} readOnly={selectedProduct ? true : false} onChange={setSearchProduct} onKeyDown={searchProductInOrder} autoFocus />
                </div>
            </div>
            <div>
                <div>
                    <TwinTrans transKey='quantity'>Cantidad</TwinTrans>
                </div>
                <RectangularInputFormatNumberSmall className='prepare_order_quantity_selector' readOnly={!selectedProduct} value={selectedProduct?.quantity || ''} onChange={(val) => changeSelectProductQuantity(val)} onKeyDown={handleAddProductTable} />
            </div>
            <InfoSelectedProduct {...{ servedOrderTD, selectedProduct }}>
                <div className='flex items-end mb-5 gap-x-20'>
                    <TwinIconToolTip classNameIcon='text-red-BA regular22' icon={faXmarkCircle} onClick={() => logic.setSelectedProduct(null)} message={t('eraseProduct', 'Borrar producto')} id={'eraseProduct'} />
                    <TwinIconToolTip classNameIcon='text-green-43 regular22' icon={faCheckCircle} onClick={addSelectedProductTable} message={t('addProduct', 'Añadir producto')} id={'acceptProduct'} />
                </div>
            </InfoSelectedProduct>
        </div>
    )
}

interface SelectorProductsOrderSingleProps extends SelectorProductsOrderLogicProps {
    label: string
    servedOrderTD: ServedOrderTDTy
}

export const SelectorProductsOrderSingle: React.FC<SelectorProductsOrderSingleProps> = ({ label, selectedProduct, servedOrderTD, filterWorksheetProductStatusId, ...logic }) => {
    const { t } = useTwinTranslation()
    const { searchProduct, setSearchProduct, searchProductInOrderWithOutQuantity } = useSelectorProductsOrderLogic({ selectedProduct, filterWorksheetProductStatusId, servedOrderTD, ...logic })
    return (
        <div className='flex items-center gap-x-20'>
            <div>
                <div>{label}</div>
                <div className='flex'>
                    <RectangularInput className='prepare_order_reference_selector' label={t('reference', 'Referencia')} value={searchProduct} onChange={setSearchProduct} onKeyDown={searchProductInOrderWithOutQuantity} autoFocus />
                </div>
            </div>
            <InfoSelectedProduct {...{ servedOrderTD, selectedProduct }} />
        </div>
    )
}

interface SelectorProductsOrderLogicProps {
    typeFieldSearch: TypeFieldSearchTyKeys
    selectedProduct?: TwinDictionary
    servedOrderTD: ServedOrderTDTy
    Order: OrderData
    filterWorksheetProductStatusId?: number
    setSelectedProduct: React.Dispatch<React.SetStateAction<TwinDictionary | null>>
    addProductTable: (product: TwinDictionary) => void
    addQuantity: (id: number, value: number) => void
}

const useSelectorProductsOrderLogic = ({ Order, selectedProduct, setSelectedProduct, servedOrderTD, addProductTable, addQuantity, typeFieldSearch, filterWorksheetProductStatusId }: SelectorProductsOrderLogicProps) => {
    const copyOrderTableData = useMemo(() => JSON.parse(JSON.stringify(Order.tableData)), [Order])
    const [searchProduct, setSearchProduct] = useState<string>('')
    const { t } = useTwinTranslation()
    const notFoundMsg = t('productNotFound', 'Producto no encontrado')
    const addedProductMsg = t('addedProduct', 'Producto añadido')
    const alreadyAddedProductMsg = t('alReadyAddedProduct', 'Ya has añadido ese producto')
    const modifiedProductMsg = t('modifiedProduct', 'Producto modificado')
    const overComeQuantityMsg = t('overComeDesiredQuantity', 'Has superado la cantidad deseada')

    const searchProductAndAddTotal = useCallback(async (event: React.KeyboardEvent<HTMLInputElement>) => {
        if (event.key === 'Enter') {
            flushSync(async () => {
                const findProduct = findProductByTypeSearch(copyOrderTableData, searchProduct, servedOrderTD, typeFieldSearch, filterWorksheetProductStatusId)
                if (findProduct) {
                    if (findProduct.completed) {
                        changeErrorMessage(alreadyAddedProductMsg)
                    } else {
                        setSelectedProduct(findProduct)
                        if (findProduct.alreadyPushed) {
                            addQuantity(findProduct?.id, findProduct.quantity)
                            changeSuccessMessage(modifiedProductMsg)
                        } else if (findProduct.quantity > 0) {
                            addProductTable(findProduct)
                            changeSuccessMessage(addedProductMsg)
                        }
                    }
                } else {
                    changeErrorMessage(notFoundMsg)
                }
                setSearchProduct('')
            })
        }
    }, [searchProduct, servedOrderTD, copyOrderTableData, notFoundMsg, setSearchProduct, alreadyAddedProductMsg, typeFieldSearch, filterWorksheetProductStatusId, addProductTable, setSelectedProduct, addQuantity, addedProductMsg, modifiedProductMsg])

    const searchProductInOrder = useCallback(async (event: React.KeyboardEvent<HTMLInputElement>) => {
        if (event.key === 'Enter') {
            flushSync(async () => {
                const findProduct = findProductByTypeSearch(copyOrderTableData, searchProduct, servedOrderTD, typeFieldSearch, filterWorksheetProductStatusId)
                if (findProduct) {
                    if (findProduct.completed) {
                        changeErrorMessage(alreadyAddedProductMsg)
                    } else {
                        setSelectedProduct({ ...findProduct })
                        // @ts-ignore
                        document.querySelector('.prepare_order_quantity_selector input')?.focus()
                    }
                } else {
                    setSelectedProduct(null)
                    changeErrorMessage(notFoundMsg)
                }
                setSearchProduct('')
            })
        }
    }, [searchProduct, servedOrderTD, copyOrderTableData, notFoundMsg, setSearchProduct, setSelectedProduct, alreadyAddedProductMsg, typeFieldSearch, filterWorksheetProductStatusId])

    const searchProductInOrderWithOutQuantity = useCallback(async (event: React.KeyboardEvent<HTMLInputElement>) => {
        if (event.key === 'Enter') {
            const findProduct = findProductByTypeSearch(copyOrderTableData, searchProduct, servedOrderTD, typeFieldSearch, filterWorksheetProductStatusId)
            let mySelectedProduct = findProduct
            if (findProduct?.alreadyPushed) {
                addQuantity(findProduct?.id, 1)
                changeSuccessMessage(modifiedProductMsg)
            } else if (findProduct) {
                if (findProduct.completed) {
                    changeErrorMessage(alreadyAddedProductMsg)
                    mySelectedProduct = null
                } else {
                    findProduct.quantity = 1
                    addProductTable(findProduct)
                    changeSuccessMessage(addedProductMsg)
                }
            } else {
                mySelectedProduct = null
                changeErrorMessage(notFoundMsg)
            }
            setSelectedProduct(mySelectedProduct)
            setSearchProduct('')
            //@ts-ignore
            document.querySelector('.prepare_order_reference_selector input')?.focus()
        }
    }, [searchProduct, servedOrderTD, copyOrderTableData, addQuantity, addProductTable, addedProductMsg, modifiedProductMsg, setSearchProduct, notFoundMsg, setSelectedProduct, alreadyAddedProductMsg, typeFieldSearch, filterWorksheetProductStatusId])

    const addSelectedProductTable = useCallback(async () => {
        if (selectedProduct) {
            const maxValue = selectedProduct?.id && servedOrderTD.selectedProducts?.[selectedProduct?.id] ? parseFloat(servedOrderTD.selectedProducts[selectedProduct?.id].desiredQuantity) - parseFloat(servedOrderTD.selectedProducts[selectedProduct?.id].quantity) : parseFloat(selectedProduct?.desiredQuantity) || 0
            selectedProduct.quantity = parseFloat(selectedProduct.quantity || '0')
            if (selectedProduct.quantity > maxValue) {
                changeErrorMessage(overComeQuantityMsg)
                return;
            }
            if (selectedProduct.alreadyPushed) {
                addQuantity(selectedProduct?.id, selectedProduct.quantity)
                changeSuccessMessage(modifiedProductMsg)
            } else if (selectedProduct.quantity > 0) {
                addProductTable(selectedProduct)
                changeSuccessMessage(addedProductMsg)
            }
            setSelectedProduct(null)
            setSearchProduct('')
            //@ts-ignore
            document.querySelector('.prepare_order_reference_selector input')?.focus()
        }
    }, [selectedProduct, addProductTable, addedProductMsg, setSearchProduct, setSelectedProduct, addQuantity, modifiedProductMsg, overComeQuantityMsg, servedOrderTD.selectedProducts])

    const handleAddProductTable = useCallback(async (event: React.KeyboardEvent<HTMLInputElement>) => {
        if (event.key === 'Enter') {
            addSelectedProductTable()
        }
    }, [addSelectedProductTable])

    const changeSelectProductQuantity = useCallback((value: string) => {
        setSelectedProduct((old) => {
            const copyOld = JSON.parse(JSON.stringify(old))
            if (copyOld) {
                copyOld.quantity = value
            }
            return copyOld
        })
    }, [setSelectedProduct])

    return { searchProduct, setSearchProduct, searchProductInOrder, handleAddProductTable, searchProductInOrderWithOutQuantity, changeSelectProductQuantity, addSelectedProductTable, searchProductAndAddTotal }
}